import { SuccessfulAddUserDialog } from '../components/users/successfulAddUserDialog';
import { AddUserDialoge } from '../components/users/addUserDialog';
import UsersFiltration from '../components/users/usersFiltration';
import UsersPageHeader from '../components/users/usersPageHeader';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import UsersGridView from '../components/users/usersGridView';
import UsersListView from '../components/users/usersListView';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import { useLocation, useNavigate } from 'react-router-dom';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
    usePersistedListParams,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import axios from 'axios';

const Users = () => {

    const location = useLocation();
    const navigate = useNavigate();
    const hasUserCreatePermission = useHasPermission('user_create');

    const [loading, setLoading] = useState(true);
    const [userId, setUserId] = useState(null);
    const [openAddUser, setOpenAddUser] = useState(false);
    const [openSuccessfulAddUser, setOpenSuccessfulAddUser] = useState(false);

    const defaultSortBy = {
        name: null,
        email: null,
        role: null,
    };
    const {
        filters,
        setFilters,
        sortBy,
        setSortBy,
        displayMode: selectedDisplayMode,
        setDisplayMode: setSelectedDisplayMode,
    } = usePersistedListParams({
        keys: {
            filters: 'userFilters',
            sortBy: 'userSortBy',
            displayMode: 'selectedUsersDisplayMode',
        },
        defaults: {
            filters: {
                debouncedSearch: '',
                selectedRegion: '',
                selectedSite: '',
                selectedPole: '',
                selectedMetier: '',
            },
            sortBy: defaultSortBy,
            displayMode: 'list',
        },
    });

    const [users, setUsers] = useState([])
    const [totalUsers, setTotalUsers] = useState('');
    const [filteredUsersCount, setFilteredUsersCount] = useState('');
    const [usersFetched, setUsersFetched] = useState(0);
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'users',
        pathname: '/dashboard/utilisateurs',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 350, list: 70 },
        resetOnFilters: filters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(filters);

    const fetchUsers = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('user', { params: { offset: adjustedOffset, limit, filters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedUsers = [];
                // Update the fetched records in their correct positions
                response.data.users.forEach((file, index) => {
                    updatedUsers[adjustedOffset + index] = file;
                });
                // Update the state with the new files array
                setUsers(updatedUsers);
                setFilteredUsersCount(response.data.filteredUsersCount);
                setTotalUsers(response.data.totalUsers);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching users:', error);
            CustomErrorLogger(error, 'fetch users in users page');
        }
    }, [offset, filters, sortBy]);;

    useEffect(() => {
        fetchUsers()
        if (location.state) {
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchUsers]);

    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Utilisateurs" list={true}>
            <UsersPageHeader setOpenAddUser={setOpenAddUser} />
            <Divider />
            {
                loading ?
                    <Box>
                        <UsersFiltration
                            filtersEmpty={filtersEmpty}
                            totalUsers={totalUsers}
                            filters={filters}
                            setFilters={setFilters}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                            filteredUsersCount={filteredUsersCount}
                            usersFetched={usersFetched}
                        />
                        <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                    </Box> :
                    totalUsers === 0 ?
                        <NoRecord
                            description="Aucun machine ajouté pour le moment"
                            buttonTitle="Ajouter une machine"
                            handleOpenDialog={() => setOpenAddUser(true)}
                            hasPermission={hasUserCreatePermission}
                        /> :
                        <Box>
                            <UsersFiltration
                                filtersEmpty={filtersEmpty}
                                totalUsers={totalUsers}
                                filters={filters}
                                setFilters={setFilters}
                                selectedDisplayMode={selectedDisplayMode}
                                setSelectedDisplayMode={setSelectedDisplayMode}
                                filteredUsersCount={filteredUsersCount}
                                usersFetched={usersFetched}
                            />

                            {selectedDisplayMode === 'grid' ? (
                                <UsersGridView
                                    scrollRef={scrollRef}
                                    filters={filters}
                                    users={users}
                                    setOffset={setOffset}
                                    filteredUsersCount={filteredUsersCount}
                                    setUsersFetched={setUsersFetched}
                                />
                            ) : (
                                <UsersListView
                                    scrollRef={scrollRef}
                                    users={users}
                                    filters={filters}
                                    sortBy={sortBy}
                                    setSortBy={setSortBy}
                                    setOffset={setOffset}
                                    filteredUsersCount={filteredUsersCount}
                                    setUsersFetched={setUsersFetched}
                                />
                            )}
                        </Box>
            }
            {openAddUser &&
                <AddUserDialoge
                    users={users}
                    setUsers={setUsers}
                    totalUsers={totalUsers}
                    setTotalUsers={setTotalUsers}
                    filteredUsersCount={filteredUsersCount}
                    setFilteredUsersCount={setFilteredUsersCount}
                    openAddUser={openAddUser}
                    setOpenAddUser={setOpenAddUser}
                    setUserId={setUserId}
                />
            }

            {openSuccessfulAddUser &&
                <SuccessfulAddUserDialog
                    openSuccessfulAddUser={openSuccessfulAddUser}
                    userId={userId}
                />
            }
        </Page>
        </ListNavigationProvider>
    );
};

export default Users;